import type { StorefrontId } from '@mentora/data-access/analytics-events'
import posthog from 'posthog-js'
import { isAnalyticsLoaded, registerStorefront } from './posthog-client'

/** `unavailable`: analytics is not configured or not booted, so there is nothing to consent to. */
export type ConsentStatus = 'granted' | 'denied' | 'pending' | 'unavailable'

// posthog-js keeps the decision but has no change callback; this store lets the
// banner, the footer link and the identity binding react to each other.
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners)
    listener()
}

export function subscribeConsent(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getConsentStatus(): ConsentStatus {
  if (!isAnalyticsLoaded())
    return 'unavailable'
  return posthog.get_explicit_consent_status()
}

/** Server snapshot: the banner never renders on the server. */
export function getServerConsentStatus(): ConsentStatus {
  return 'unavailable'
}

export function acceptConsent(): void {
  if (!isAnalyticsLoaded())
    return
  posthog.opt_in_capturing()
  notify()
}

export function rejectConsent(): void {
  if (!isAnalyticsLoaded())
    return
  posthog.opt_out_capturing()
  notify()
}

/** Back to `pending`, so the banner asks again. */
export function reopenConsent(): void {
  if (!isAnalyticsLoaded())
    return
  posthog.clear_opt_in_out_capturing()
  notify()
}

/**
 * New anonymous identity after sign-out. `posthog.reset()` also wipes the
 * consent decision and super properties, so a bare reset would re-ask a user
 * who already accepted and drop the `storefront` tag; both are restored here.
 * Order matters: reset first, then opt back in (an opt-in before a reset is lost).
 */
export function resetAnalyticsIdentity(storefront: StorefrontId): void {
  if (!isAnalyticsLoaded())
    return
  const status = posthog.get_explicit_consent_status()
  posthog.reset()
  registerStorefront(storefront)
  if (status === 'granted')
    posthog.opt_in_capturing()
  else if (status === 'denied')
    posthog.opt_out_capturing()
  notify()
}
